import { authenticate } from "@/controllers/auth";
import { getUserUniverses } from "@/controllers/universes";
import { customViewsState, subscriptionOfferingsState, universesState, userState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import useOpenSubscriptionForm from "./useOpenSubscriptionForm";
import useAppSubscriberUpgrade from "./useAppSubscriberUpgrade";

const useGetUserData = (isAuthenticated = true) => {
    const { pathname, replace } = useRouter();
    const setUniverses = useSetRecoilState(universesState);
    const setUser = useSetRecoilState(userState);
    const [loading, setLoading] = useState(true);
    const setCustomViews = useSetRecoilState(customViewsState);
    const offerings = useRecoilValue(subscriptionOfferingsState);
    const { openSubscriptionForm } = useOpenSubscriptionForm();
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();

    const fetchData = useMemo(() => {
        if (!isAuthenticated) return;

        return async () => {
            try {
                const [user, universes] = await Promise.all([authenticate(), getUserUniverses()]);
                handleAppSubscriberUpgrade({
                    openPaymentModal: false,
                    openForm: false,
                    closeModalAfterDelay: false,
                    user,
                });
                // For overdue users we open the subscription form so they
                // can renew. App subscribers are *already* having their
                // subscription bootstrapped via `handleAppSubscriberUpgrade`
                // (which fires `restartSubscription("APPDISC")` on their
                // behalf) — calling `openSubscriptionForm()` here would
                // double-fire `/API/v2/user/new_subscription`, once with
                // APPDISC and once without, both of which take ~3s on the
                // backend. So skip the no-code call for app subscribers.
                if (user.overdue && !user.app_subscriber_only && (!isOrtexApp() || offerings)) {
                    openSubscriptionForm();
                }

                setUniverses(universes);
                setUser(user);

                if (user.settings.module_settings) {
                    if (user.settings.module_settings["custom_views->tabs"] && user.settings.module_settings["custom_views->tabs"].length > 0) {
                        setCustomViews(user.settings.module_settings["custom_views->tabs"]);
                    } else {
                        setCustomViews([{ id: uuidv4(), name: "Default Custom View", layout: [], mobileLayout: [], modules: [], lastUpdated: dayjs().format("DD MMM YY") }]);
                    }
                }

                if (["/login", "/login-legacy", "/cognito-login"].includes(pathname) && user.email !== "visitor@ortex.com") {
                    // Only redirect home if Amplify also has a session. If the Django cookie
                    // is valid but Amplify tokens are gone, it's a stale cookie from a failed
                    // backend logout — show the login form instead of bouncing back.
                    // Mobile app authenticates via backend session only (no Amplify tokens),
                    // so skip the Amplify check there — unless the user just explicitly logged out.
                    const hasAmplifySession = (isOrtexApp() && !!localStorage.getItem("ortexUser")) || Object.keys(localStorage).some(
                        (key) => key.startsWith("CognitoIdentityServiceProvider") && key.endsWith(".accessToken")
                    );
                    if (hasAmplifySession) {
                        // Carry affiliate promo/checkout params through to the payment
                        // page so an already-logged-in user who lands on an affiliate
                        // /login link still gets the discounted upgrade flow instead of
                        // being dropped on the home page (params lost).
                        const sp = new URLSearchParams(window.location.search);
                        const parts = ["promo", "t", "v", "checkout"]
                            .filter((k) => sp.get(k))
                            .map((k) => `${k}=${encodeURIComponent(sp.get(k) as string)}`);
                        replace(parts.length ? `/upgrade?${parts.join("&")}` : "/");
                    } else {
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                }
            } catch (error) {
                setUser(null);
                if (pathname.includes("/login")) {
                    Object.keys(localStorage).forEach((key) => {
                        if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                            localStorage.removeItem(key);
                        }
                    });
                    setLoading(false);
                } else {
                    replace("/login");
                }
            }
        };
    }, [isAuthenticated, isOrtexApp(), offerings]);

    useEffect(() => {
        if (fetchData) {
            fetchData();
        }
    }, [fetchData]);

    return { userLoading: loading };
};

export default useGetUserData;
